import React from 'react';
import { Document, Page, Text, View, StyleSheet, Font, Image, Svg, Circle, Line } from '@react-pdf/renderer';
import coreVistaLogo from '../assets/corevista_logo.png';
import DejaVuSans from '../fonts/dejavu-sans.book.ttf';
import DejaVuSansBold from '../fonts/dejavu-sans.bold.ttf';

// Register fonts
Font.register({
    family: 'dejavu-sans',
    fonts: [
        { src: DejaVuSans, fontWeight: 'normal' },
        { src: DejaVuSansBold, fontWeight: 'bold' }
    ]
});

const NAVY = '#1a237e';
const STAMP_COLOR = '#1a237e';
const TEXT_DARK = '#333';
const TEXT_LIGHT = '#444';
const BORDER_COLOR = '#eee';

const styles = StyleSheet.create({
    page: {
        paddingVertical: 35,
        paddingHorizontal: 40,
        fontFamily: 'dejavu-sans',
        fontSize: 11.5,
        color: TEXT_DARK,
        position: 'relative'
    },
    watermark: {
        position: 'absolute',
        top: 350,
        left: 100,
        fontSize: 70,
        color: 'rgba(26, 35, 126, 0.04)',
        transform: 'rotate(-30deg)',
        fontWeight: 'bold',
        zIndex: -1
    },
    headerContainer: {
        marginBottom: 10,
    },
    headerRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 10,
    },
    title: {
        fontSize: 24,
        fontWeight: 'bold',
        color: NAVY,
        textAlign: 'left',
        textTransform: 'uppercase'
    },
    headerLogo: {
        width: 240,
        height: 90,
        objectFit: 'contain',
        marginRight: -75
    },
    companySection: { marginBottom: 0 },
    companyName: { fontSize: 13, fontWeight: 'bold', color: NAVY, marginBottom: 4 },
    textLine: { fontSize: 11, marginBottom: 2, color: TEXT_LIGHT },
    textLineRight: { fontSize: 11, marginBottom: 2, color: TEXT_LIGHT, textAlign: 'right' },
    boldText: { fontWeight: 'bold' },

    billToSection: {
        marginBottom: 12,
        padding: 8,
        borderLeftWidth: 3,
        borderLeftColor: NAVY
    },
    billToTitle: { fontSize: 12, fontWeight: 'bold', marginBottom: 6, color: NAVY, textTransform: 'uppercase' },

    section: { marginBottom: 10 },
    sectionTitle: {
        fontSize: 13,
        fontWeight: 'bold',
        color: NAVY,
        marginBottom: 6,
        borderBottomWidth: 1,
        borderBottomColor: BORDER_COLOR,
        paddingBottom: 2,
        textTransform: 'uppercase'
    },
    paragraph: {
        fontSize: 11.5,
        lineHeight: 1.4,
        color: TEXT_DARK,
        textAlign: 'justify',
        marginBottom: 5
    },

    listRow: { flexDirection: 'row', marginBottom: 3, paddingLeft: 8 },
    listDot: { width: 15, fontSize: 11.5, color: NAVY },
    listContent: { flex: 1, fontSize: 11.5, lineHeight: 1.4, color: TEXT_DARK },

    signatureGrid: {
        marginTop: 15,
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    signatureBlock: { width: '45%' },
    sigLine: {
        marginTop: 15,
        borderBottomWidth: 1,
        borderBottomColor: '#333',
        marginBottom: 4
    },
    sigCaption: { fontSize: 9, color: '#777' },

    // Anti-clone rectangular stamp with faded ink
    stampContainer: {
        marginTop: 15,
        alignSelf: 'center',
        opacity: 0.6,
    },
    stampOuter: {
        borderWidth: 3,
        borderColor: STAMP_COLOR,
        padding: 3,
    },
    stampInner: {
        borderWidth: 1.5,
        borderColor: STAMP_COLOR,
        paddingVertical: 5,
        paddingHorizontal: 14,
        alignItems: 'center',
    },
    stampCompany: {
        fontSize: 6,
        fontWeight: 'bold',
        color: STAMP_COLOR,
        letterSpacing: 2,
        textTransform: 'uppercase',
        textAlign: 'center',
        marginBottom: 1,
    },
    stampDivider: {
        width: '100%',
        borderBottomWidth: 0.8,
        borderBottomColor: STAMP_COLOR,
        marginVertical: 2,
    },
    stampCertified: {
        fontSize: 11,
        fontWeight: 'bold',
        color: STAMP_COLOR,
        letterSpacing: 3,
        textTransform: 'uppercase',
        textAlign: 'center',
    },
    stampDate: {
        fontSize: 5,
        color: STAMP_COLOR,
        textAlign: 'center',
        marginTop: 1,
        letterSpacing: 0.5,
    },
    stampLocation: {
        fontSize: 6,
        fontWeight: 'bold',
        color: STAMP_COLOR,
        letterSpacing: 1.5,
        textTransform: 'uppercase',
        textAlign: 'center',
        marginTop: 1,
    },
    stampMicro: {
        fontSize: 3.5,
        color: STAMP_COLOR,
        textAlign: 'center',
        letterSpacing: 0.3,
        marginTop: 2,
    },

    footer: {
        position: 'absolute',
        bottom: 25,
        left: 40,
        right: 40,
        borderTopWidth: 1,
        borderTopColor: BORDER_COLOR,
        paddingTop: 8,
        flexDirection: 'row',
        justifyContent: 'space-between'
    },
    footerText: { fontSize: 9, color: '#999' }
});

// Generate a unique verification hash per document
const generateVerifyCode = (schoolId) => {
    const ts = Date.now().toString(36).toUpperCase();
    const id = (schoolId || 'XXXXXX').slice(-6).toUpperCase();
    return `CV-${id}-${ts}`;
};

// Anti-clone rectangular stamp with per-document verification
const OfficialStamp = ({ schoolId, dateStr }) => {
    const verifyCode = generateVerifyCode(schoolId);
    return (
        <View style={styles.stampContainer}>
            <View style={styles.stampOuter}>
                <View style={styles.stampInner}>
                    <Text style={styles.stampCompany}>CoreVista Technologies</Text>
                    <View style={styles.stampDivider} />
                    <Text style={styles.stampCertified}>CERTIFIED</Text>
                    <Text style={styles.stampDate}>{dateStr}</Text>
                    <View style={styles.stampDivider} />
                    <Text style={styles.stampLocation}>Accra — Ghana</Text>
                    <Text style={styles.stampMicro}>VERIFY: {verifyCode}</Text>
                </View>
            </View>
        </View>
    );
};

const Bullet = ({ children }) => (
    <View style={styles.listRow}>
        <Text style={styles.listDot}>•</Text>
        <Text style={styles.listContent}>{children}</Text>
    </View>
);

const ServiceAgreementPDF = ({ school }) => {
    const today = new Date();
    const dateStr = today.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
    });
    const sName = school?.name || '__________________________';
    const sAddr = school?.address || '__________________________';

    return (
        <Document>
            {/* PAGE 1 */}
            <Page size="A4" style={styles.page}>
                <View fixed style={styles.watermark}>
                    <Text>COREVISTA</Text>
                </View>

                <View style={styles.headerContainer}>
                    <View style={styles.headerRow}>
                        <Text style={styles.title}>Service Agreement</Text>
                        <Image src={coreVistaLogo} style={styles.headerLogo} />
                    </View>

                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <View style={styles.companySection}>
                            <Text style={styles.companyName}>CoreVista Technologies</Text>
                            <Text style={styles.textLine}>Accra, Ghana</Text>
                            <Text style={styles.textLine}>Phone: +233 5464 0478</Text>
                            <Text style={styles.textLine}>Email: contactcorevista@gmail.com</Text>
                        </View>
                        <View style={{ alignItems: 'flex-end' }}>
                            <Text style={styles.textLineRight}>
                                <Text style={styles.boldText}>Document Ref: </Text>
                                SLA-{school?._id?.slice(-5).toUpperCase() || 'REF'}
                            </Text>
                            <Text style={styles.textLineRight}>
                                <Text style={styles.boldText}>Date: </Text>
                                {dateStr}
                            </Text>
                        </View>
                    </View>
                </View>

                <View style={styles.billToSection}>
                    <Text style={styles.billToTitle}>Agreement Between:</Text>
                    <Text style={[styles.textLine, styles.boldText, { fontSize: 11, color: NAVY }]}>
                        {sName}
                    </Text>
                    <Text style={styles.textLine}>Address: {sAddr}</Text>
                    <Text style={styles.textLine}>Attention: The Principal / Administrator</Text>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>1. Purpose of Agreement</Text>
                    <Text style={styles.paragraph}>
                        This Service Level Agreement defines the standard operating procedures and technical support
                        framework between CoreVista Technologies ("Service Provider") and the aforementioned Institution
                        ("The School") for the deployment and maintenance of the CoreVista School management platform.
                    </Text>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>2. Scope of Services</Text>
                    <Bullet>Deployment of Student, Teacher, and Parent portals.</Bullet>
                    <Bullet>Real-time attendance tracking and academic records management.</Bullet>
                    <Bullet>Term-based configuration and administrative training for staff.</Bullet>
                    <Bullet>Cloud hosting and data backup maintenance.</Bullet>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>3. Subscription Fees</Text>
                    <Text style={styles.paragraph}>
                        The School agrees to a subscription fee of <Text style={styles.boldText}>₵1500.00 per academic term</Text>. All payments must be received
                        prior to system activation or renewal.
                    </Text>
                    <Text style={styles.paragraph}>
                        The Service Provider reserves the right to revise subscription fees periodically to reflect
                        improvements in service delivery, infrastructure costs, and platform enhancements. Schools will
                        be notified of any fee adjustments at least <Text style={styles.boldText}>30 days</Text> before
                        the start of the next billing cycle.
                    </Text>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>4. Data Ownership & Security</Text>
                    <Text style={styles.paragraph}>
                        All data uploaded by the School remains the exclusive property of the Institution.
                        CoreVista shall not sell, share, or redistribute any data without explicit written consent.
                    </Text>
                </View>

                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}>© {today.getFullYear()} CoreVista Technologies</Text>
                    <Text style={styles.footerText}>Page 1 of 2</Text>
                </View>
            </Page>

            {/* PAGE 2 */}
            <Page size="A4" style={styles.page}>
                <View fixed style={styles.watermark}>
                    <Text>COREVISTA</Text>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>5. Technical Support</Text>
                    <Text style={styles.paragraph}>
                        The Service Provider offers technical assistance via WhatsApp and Email during standard business
                        hours (8:00 AM — 4:00 PM). Emergency maintenance is prioritized to minimize school downtime.
                    </Text>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>6. System Availability</Text>
                    <Text style={styles.paragraph}>
                        CoreVista aims for a 99.5% system uptime. Scheduled maintenance will be communicated at least
                        48 hours in advance. Failures due to local internet connectivity are excluded from Provider liability.
                    </Text>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>7. Termination Policy</Text>
                    <Text style={styles.paragraph}>
                        Either party may terminate this agreement with 30 days' written notice. Upon termination,
                        the school has 60 days to export their academic data before the archive is permanently deleted.
                    </Text>
                </View>

                <View style={styles.section}>
                    <Text style={styles.sectionTitle}>8. Contractual Agreement</Text>
                    <Text style={styles.paragraph}>
                        By signing below, both parties acknowledge and accept the terms and conditions outlined in this
                        Document Ref: SLA-{school?._id?.slice(-5).toUpperCase() || 'REF'}.
                    </Text>

                    <View style={styles.signatureGrid}>
                        <View style={styles.signatureBlock}>
                            <Text style={styles.boldText}>For CoreVista Technologies</Text>
                            <Text style={styles.textLine}>Director: Evans Osei</Text>
                            <View style={styles.sigLine} />
                            <Text style={styles.sigCaption}>Authorised Signature & Date</Text>
                            <OfficialStamp schoolId={school?._id} dateStr={dateStr} />
                        </View>
                        <View style={styles.signatureBlock}>
                            <Text style={styles.boldText}>For {sName}</Text>
                            <Text style={styles.textLine}>Name: </Text>
                            <View style={styles.sigLine} />
                            <Text style={styles.sigCaption}>Signature, Date & Official Stamp</Text>
                        </View>
                    </View>
                </View>

                <View style={styles.footer} fixed>
                    <Text style={styles.footerText}></Text>
                    <Text style={styles.footerText}>Page 2 of 2</Text>
                </View>
            </Page>
        </Document>
    );
};

export default ServiceAgreementPDF;
